import { Component } from '@theme/component';
import { DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

const AUTO_DISMISS_DELAY = 5000;

const selectors = {
  DIALOG: 'dialog-component',
  ERROR_MESSAGE: '.cart-error__message',
  CART_DRAWER: 'cart-drawer-component',
};

/**
 * A custom element that manages the cart error toaster.
 * Listens for CartErrorEvent on document, auto-dismisses after 5 seconds,
 * and also dismisses when the cart drawer closes.
 *
 * @extends {Component}
 */
export class CartError extends Component {
  /** @type {AbortController | null} */
  #dismissController = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #dismissTimeout = undefined;

  /** @type {AbortController | null} */
  #cartErrorController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#cartErrorController = new AbortController();
    const { signal } = this.#cartErrorController;
    document.addEventListener(ThemeEvents.cartError, this.#handleCartErrorEvent, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cartErrorController?.abort();
    this.#cartErrorController = null;
    this.#clearDismiss();
  }

  /** @param {Event} event */
  #handleCartErrorEvent = (event) => {
    const { message } = /** @type {import('@theme/events').CartErrorEvent} */ (event).detail.data;
    this.showError(message);
  };

  /**
   * Shows the error toaster with the given message.
   * showPopover() is deferred to requestAnimationFrame so it runs after
   * dialog.showModal() (which the cart drawer also schedules in rAF),
   * ensuring the popover sits above the dialog in the top layer.
   * @param {string} message
   */
  showError(message) {
    const errorMessage = this.querySelector(selectors.ERROR_MESSAGE);
    if (errorMessage) errorMessage.textContent = message;

    this.#clearDismiss();

    requestAnimationFrame(() => {
      if (!this.matches(':popover-open')) this.showPopover();
    });

    this.#dismissController = new AbortController();
    const { signal } = this.#dismissController;

    this.#dismissTimeout = setTimeout(() => this.dismissError(), AUTO_DISMISS_DELAY);

    const dialogEl = document.querySelector(`${selectors.CART_DRAWER} ${selectors.DIALOG}`);
    dialogEl?.addEventListener(DialogCloseEvent.eventName, () => this.dismissError(), {
      signal,
      once: true,
    });
  }

  dismissError() {
    if (this.matches(':popover-open')) this.hidePopover();

    this.#clearDismiss();
  }

  #clearDismiss() {
    this.#dismissController?.abort();
    this.#dismissController = null;
    clearTimeout(this.#dismissTimeout);
    this.#dismissTimeout = undefined;
  }
}

if (!customElements.get('cart-error')) {
  customElements.define('cart-error', CartError);
}
